/**
 * Skeepers Rating Manager - Moon Moon Agency
 * Remplit les notes produit (mm-rating, ligne de note du side panel…) depuis l'API publique
 * Avis Vérifiés, sans metafield ni widget.
 *
 * Contrat DOM :
 *   [data-skp-rating="<product.id>"]  racine, masquée (visibility) tant que l'API n'a pas répondu
 *   [data-skp-score] / [data-skp-count]  emplacements remplis (note sur 1 décimale / nombre d'avis)
 *   data-skp-format="…{value}…"  modèle du texte de l'emplacement (défaut « {value} »)
 *   data-skp-wait-for="<sélecteur>"  racine révélée seulement quand cet élément a du contenu rendu
 *     (hauteur > 0). Sinon, data-skp-empty une fois le délai écoulé depuis son arrivée à l'écran :
 *     un widget qui ne se charge pas (bloqueur, panne) ne laisse pas un titre seul.
 * Le produit n'a pas d'avis : la racine reçoit data-skp-empty (display: none).
 */

/*********************************************************************************************
 * ⭐ SKEEPERS RATING MANAGER
 */
const SkeepersRatingManager = {
	config: {
		apiUrl: 'https://api-product-reviews.cxr.skeepers.io/v1',
		websiteId: 'b371d8fb-9a2a-f1f4-ed18-82ae08a89d76',
		siteRef: 'site',
		siteUrl: 'https://cl.avis-verifies.com/fr/cache/b/3/7/b371d8fb-9a2a-f1f4-ed18-82ae08a89d76/AWS/b371d8fb-9a2a-f1f4-ed18-82ae08a89d76_infosite.txt',
		batchSize: 30, // même taille de lot que le widget Skeepers
		debounceDelay: 100,
		waitTimeout: 8000, // attente du contenu d'un widget, comptée depuis son arrivée à l'écran
		selectors: {
			root: '[data-skp-rating]',
			pending: '[data-skp-rating]:not([data-skp-ready]):not([data-skp-empty]):not([data-skp-loading])',
			score: '[data-skp-score]',
			count: '[data-skp-count]',
		},
		attributes: {
			ref: 'data-skp-rating',
			format: 'data-skp-format',
			waitFor: 'data-skp-wait-for',
			loading: 'data-skp-loading',
			ready: 'data-skp-ready',
			empty: 'data-skp-empty',
		},
	},

	ratings: new Map(), // ref → { average, nb_total } | null (réponses reçues)
	_guards: new WeakMap(), // racine → arrêt de l'attente de contenu (data-skp-wait-for)

	/**
	 * Initialize the module
	 */
	init() {
		this._observe();
		this.refresh();
		window.log('info', '⭐ [SKEEPERS_RATING]', 'Initialized');
	},

	/**
	 * Hydrate toutes les notes pas encore traitées (appelable après une injection HTML)
	 */
	refresh() {
		const roots = [...window.getElements(this.config.selectors.pending)];
		if (!roots.length) return;
		this._hydrate(roots);
	},

	/**
	 * Repointe une racine déjà traitée sur un autre produit (ex. sélecteur de modèle du comparateur)
	 */
	update(root, ref) {
		const { attributes: attr } = this.config;
		this._guards.get(root)?.();
		root.setAttribute(attr.ref, ref);
		[attr.loading, attr.ready, attr.empty].forEach((name) => root.removeAttribute(name));
		this._hydrate([root]);
	},

	/**
	 * Cartes, panneaux et modales ajoutés après le chargement : un observer suffit,
	 * pas de câblage par module. On ignore les mutations sans nœud ajouté (dont les nôtres).
	 */
	_observe() {
		const debounced = window.debounce(() => this.refresh(), this.config.debounceDelay);
		new MutationObserver((mutations) => {
			const hasAddedElement = mutations.some((m) => [...m.addedNodes].some((n) => n.nodeType === Node.ELEMENT_NODE));
			if (hasAddedElement) debounced();
		}).observe(document.body, { childList: true, subtree: true });
	},

	async _hydrate(roots) {
		const attr = this.config.attributes;
		const refs = [...new Set(roots.map((root) => root.getAttribute(attr.ref).trim()).filter(Boolean))];

		// Marque tout de suite : un refresh qui arrive pendant le fetch ne retraite pas ces racines
		// (attribut à part : data-skp-ready lève le masquage, il ne doit venir qu'une fois la note posée)
		roots.forEach((root) => root.setAttribute(attr.loading, ''));
		await this._fetchRatings(refs.filter((ref) => !this.ratings.has(ref)));

		roots.forEach((root) => this._apply(root, this.ratings.get(root.getAttribute(attr.ref).trim())));
	},

	async _fetchRatings(refs) {
		const { apiUrl, websiteId, batchSize, siteRef } = this.config;
		const productRefs = refs.filter((ref) => ref !== siteRef);
		const chunks = [];
		for (let i = 0; i < productRefs.length; i += batchSize) chunks.push(productRefs.slice(i, i + batchSize));

		await Promise.all([
			...(refs.includes(siteRef) ? [this._fetchSiteRating()] : []),
			...chunks.map(async (chunk) => {
				try {
					const response = await fetch(`${apiUrl}/websites/${websiteId}/products/ratings?ref=${chunk.map(encodeURIComponent).join(',')}`);
					if (!response.ok) throw new Error(`HTTP ${response.status}`);
					const { data } = await response.json();
					chunk.forEach((ref) => this.ratings.set(ref, data?.[ref] ?? null));
				} catch (e) {
					// Panne API : pas de note plutôt qu'une fausse, et on ne met rien en cache (retentera au prochain chargement)
					window.log('warn', '⭐ [SKEEPERS_RATING]', 'Fetch failed', { error: e.message });
				}
			}),
		]);
	},

	async _fetchSiteRating() {
		const { siteRef, siteUrl } = this.config;
		try {
			const response = await fetch(siteUrl);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const [count, average] = (await response.text()).trim().split(';').map(Number);
			this.ratings.set(siteRef, count > 0 && average > 0 ? { average, nb_total: count } : null);
		} catch (e) {
			window.log('warn', '⭐ [SKEEPERS_RATING]', 'Site rating fetch failed', { error: e.message });
		}
	},

	_apply(root, rating) {
		const { attributes: attr, selectors } = this.config;

		if (!rating || !rating.nb_total) {
			this._settle(root, attr.empty);
			return;
		}

		this._fill(root.querySelectorAll(selectors.score), Number(rating.average).toFixed(1));
		this._fill(root.querySelectorAll(selectors.count), rating.nb_total.toLocaleString(document.documentElement.lang || undefined));

		const waitFor = root.getAttribute(attr.waitFor);
		if (waitFor) this._awaitContent(root, waitFor);
		else this._settle(root, attr.ready);
	},

	_settle(root, state) {
		root.removeAttribute(this.config.attributes.loading);
		root.setAttribute(state, '');
	},

	/**
	 * Révèle la racine quand l'élément attendu a du contenu rendu. Le compte à rebours ne démarre
	 * qu'à son arrivée à l'écran : le widget (chargé à la visibilité) n'a aucune raison d'être
	 * rendu avant. Pendant l'attente la racine reste masquée (place gardée) et « en cours ».
	 */
	_awaitContent(root, selector) {
		const { attributes: attr, waitTimeout } = this.config;
		const target = root.querySelector(selector);
		if (!target) {
			this._settle(root, attr.ready);
			return;
		}

		let timer;
		const stop = (state) => {
			resizeObserver.disconnect();
			viewObserver.disconnect();
			clearTimeout(timer);
			this._guards.delete(root);
			if (state) this._settle(root, state);
		};
		const resizeObserver = new ResizeObserver(() => {
			if (target.getBoundingClientRect().height > 0) stop(attr.ready);
		});
		const viewObserver = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) return;
			viewObserver.disconnect();
			timer = setTimeout(() => stop(attr.empty), waitTimeout);
		});

		this._guards.set(root, stop);
		resizeObserver.observe(target);
		viewObserver.observe(target);
	},

	_fill(slots, value) {
		slots.forEach((slot) => {
			const format = slot.getAttribute(this.config.attributes.format) || '{value}';
			slot.textContent = format.replace('{value}', value);
		});
	},
};

window.SkeepersRatingManager = SkeepersRatingManager; // interop : refresh() après injection HTML

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SkeepersRatingManager.init());
} else {
	SkeepersRatingManager.init();
}
